import { Link, useNavigate } from "react-router-dom";
import { Plus, List, Briefcase, Share2 } from "lucide-react";
import { useI18n } from "../i18n/I18nProvider";

/**
 * "What do you want to do next?" panel — shown after saving/updating a resource.
 * Four simple choices to guide the user forward without thinking.
 *
 * Props:
 *  - resourceType: "material" | "machine" | "inventory"
 *  - resourceId: string (current resource's id)
 *  - onShare: () => void  optional callback to scroll/focus the visibility picker
 */
export default function NextActionsPanel({ resourceType = "material", resourceId, onShare }) {
  const { t } = useI18n();
  const navigate = useNavigate();

  // Per-resource-type URLs (uses existing routes in App.js)
  const cfg = {
    material:  { addNew: "/listings/new", list: "/marketplace" },
    machine:   { addNew: "/machines",     list: "/machines"    },
    inventory: { addNew: "/inventory",    list: "/inventory"   },
  }[resourceType] || { addNew: "/listings/new", list: "/marketplace" };

  const actions = [
    {
      key: "add",
      Icon: Plus,
      label: t("next.addNew"),
      onClick: () => navigate(cfg.addNew),
      testid: "next-add-new",
    },
    {
      key: "view",
      Icon: List,
      label: t("next.viewAll"),
      onClick: () => navigate(cfg.list),
      testid: "next-view-all",
    },
    {
      key: "project",
      Icon: Briefcase,
      label: t("next.assignProject"),
      onClick: () => navigate("/projects"),
      testid: "next-assign-project",
    },
    {
      key: "share",
      Icon: Share2,
      label: t("next.share"),
      onClick: () => {
        if (onShare) return onShare();
        // Fallback: scroll the visibility picker into view if present
        const el = document.querySelector('[data-testid="visibility-picker"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      },
      testid: "next-share",
    },
  ];

  return (
    <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-4 sm:p-5" data-testid="next-actions-panel">
      <div className="label-tech mb-1">{t("next.title")}</div>
      <div className="text-xs text-[var(--ink-500)] mb-4">{t("next.lead")}</div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {actions.map(({ key, Icon, label, onClick, testid }) => (
          <button
            key={key}
            type="button"
            onClick={onClick}
            data-testid={testid}
            className="text-left p-3 border-2 border-[var(--ink-200)] bg-[var(--surface)] hover:border-[var(--ink)] hover:bg-[var(--surface-alt)] transition-all"
          >
            <span
              className="inline-flex items-center justify-center w-9 h-9 mb-2"
              style={{ background: "var(--ink)", color: "var(--bone)" }}
            >
              <Icon size={16} />
            </span>
            <div className="text-sm font-semibold leading-tight">{label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
